import Holidays from 'date-holidays';
import { normalizarStatusRecurso, RECURSO_STATUS } from '@/utils/recursosConstants';

const hd = new Holidays('BR');

const feriadosNacionais = (() => {
  const ano = new Date().getFullYear();
  return hd
    .getHolidays(ano)
    .concat(hd.getHolidays(ano + 1))
    .filter(feriado => feriado.type === 'public')
    .map(feriado => new Date(feriado.date));
})();

export const PRAZO_RAZOES_DIAS_UTEIS = 3;
export const PRAZO_CONTRARRAZOES_DIAS_UTEIS = 3;
export const PRAZO_CONSIDERACAO_JULGAMENTO_DIAS_UTEIS = 10;

export const startOfDay = date => {
  const normalizada = new Date(date);
  normalizada.setHours(0, 0, 0, 0);
  return normalizada;
};

/** Meio-dia local — evita deslocamento de dia no b-datepicker (Buefy). */
export const dateAtNoon = date => {
  const normalizada = startOfDay(date);
  normalizada.setHours(12, 0, 0, 0);
  return normalizada;
};

export const maxDate = (a, b) => (startOfDay(a) >= startOfDay(b) ? startOfDay(a) : startOfDay(b));

const isFeriado = date =>
  feriadosNacionais.some(feriado => feriado.toDateString() === new Date(date).toDateString());

const isBusinessDay = date => {
  const dia = new Date(date).getDay();
  return dia !== 0 && dia !== 6 && !isFeriado(date);
};

/** Avança N dias úteis — paridade com `businessDays.ts` / `Dados.vue`. */
export function addBusinessDays(from, days) {
  const data = new Date(from);
  data.setHours(0, 0, 0, 0);
  let diasUteis = 0;

  while (diasUteis < days) {
    data.setDate(data.getDate() + 1);
    if (isBusinessDay(data)) diasUteis += 1;
  }

  return data;
}

export function calcularPrazosRecursaisPadrao(d0 = new Date()) {
  const referencia = startOfDay(d0);
  const dataLimiteRazoes = addBusinessDays(referencia, PRAZO_RAZOES_DIAS_UTEIS);
  const dataLimiteContrarrazoes = addBusinessDays(dataLimiteRazoes, PRAZO_CONTRARRAZOES_DIAS_UTEIS);
  const dataLimiteJulgamento = addBusinessDays(
    dataLimiteContrarrazoes,
    PRAZO_CONSIDERACAO_JULGAMENTO_DIAS_UTEIS,
  );

  return {
    razoesDe: referencia,
    razoes: dataLimiteRazoes,
    contrarrazoesDe: dataLimiteRazoes,
    contrarrazoes: dataLimiteContrarrazoes,
    julgamentoDe: dataLimiteContrarrazoes,
    julgamento: dataLimiteJulgamento,
  };
}

export const proximoDia = (referencia = new Date()) => {
  const dia = startOfDay(referencia);
  dia.setDate(dia.getDate() + 1);
  return dia;
};

/** Início da janela seguinte: 00:00:00 do dia civil após o prazo anterior (paridade com API). */
export const inicioDoDiaPrazo = date => {
  const normalizada = startOfDay(date);
  normalizada.setHours(0, 0, 0, 0);
  return normalizada;
};

/** @deprecated Use {@link inicioDoDiaPrazo}. */
export const primeiroSegundoDoDia = inicioDoDiaPrazo;

export const fimDoDiaPrazo = date => {
  if (!date) return null;
  const normalizada = startOfDay(date);
  normalizada.setHours(23, 59, 59, 999);
  return normalizada;
};

const truncarSegundo = date => {
  const normalizada = new Date(date.getTime());
  normalizada.setMilliseconds(0);
  return normalizada;
};

const isFimPadraoPrazoRecursal = date =>
  date.getHours() === 23 && date.getMinutes() === 59 && date.getSeconds() === 59;

const isInicioPadraoPrazoRecursal = date =>
  date.getHours() === 0 &&
  date.getMinutes() === 0 &&
  (date.getSeconds() === 0 || date.getSeconds() === 1);

const isMeiaNoiteLocal = date =>
  date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;

/** Preserva instante de encerramento antecipado; normaliza limites de dia inteiro. */
export const normalizarFimPrazoRecursal = dataLimite => {
  if (!dataLimite) return null;
  const normalizada =
    dataLimite instanceof Date ? new Date(dataLimite.getTime()) : new Date(dataLimite);
  if (Number.isNaN(normalizada.getTime())) return normalizada;

  if (isFimPadraoPrazoRecursal(normalizada) || isMeiaNoiteLocal(normalizada)) {
    normalizada.setHours(23, 59, 59, 999);
    return normalizada;
  }

  return truncarSegundo(normalizada);
};

/**
 * Início da janela seguinte após um prazo.
 * - Fim padrão (23:59:59 / meia-noite) → 00:00:00 do dia civil seguinte.
 * - Encerramento antecipado → segundo imediatamente posterior (paridade com API).
 */
export const inicioJanelaRecursalAposPrazo = dataLimiteAnterior => {
  if (!dataLimiteAnterior) return null;
  const fim = normalizarFimPrazoRecursal(dataLimiteAnterior);
  if (!fim || Number.isNaN(fim.getTime())) return null;

  if (isFimPadraoPrazoRecursal(fim) || isMeiaNoiteLocal(fim)) {
    return inicioDoDiaPrazo(proximoDia(fim));
  }

  const inicio = truncarSegundo(fim);
  inicio.setSeconds(inicio.getSeconds() + 1);
  return inicio;
};

export const normalizarInicioPrazoRecursalExibicao = date => {
  if (!date) return null;
  const normalizada = new Date(date);
  if (isInicioPadraoPrazoRecursal(normalizada) || isMeiaNoiteLocal(normalizada)) {
    return inicioDoDiaPrazo(normalizada);
  }

  return truncarSegundo(normalizada);
};

/** Exibição do fim: mesma regra de negócio — não força 23:59:59 sobre antecipação. */
export const normalizarFimPrazoRecursalExibicao = date => normalizarFimPrazoRecursal(date);

export const montarResumoEtapasRecursais = ({
  d0 = new Date(),
  fimRazoes = null,
  fimContrarrazoes = null,
  fimJulgamento = null,
} = {}) => {
  const inicioRazoes = normalizarInicioPrazoRecursalExibicao(d0);
  const fimRazoesNormalizado = normalizarFimPrazoRecursalExibicao(fimRazoes);
  const fimContrarrazoesNormalizado = normalizarFimPrazoRecursalExibicao(fimContrarrazoes);
  const fimJulgamentoNormalizado = normalizarFimPrazoRecursalExibicao(fimJulgamento);
  const inicioContrarrazoes = fimRazoesNormalizado
    ? inicioJanelaRecursalAposPrazo(fimRazoesNormalizado)
    : null;
  const inicioJulgamento = fimContrarrazoesNormalizado
    ? inicioJanelaRecursalAposPrazo(fimContrarrazoesNormalizado)
    : null;

  return [
    {
      etapa: 'Prazo de Razões',
      inicio: inicioRazoes,
      fim: fimRazoesNormalizado,
    },
    {
      etapa: 'Prazo de Contrarrazões',
      inicio: inicioContrarrazoes,
      fim: fimContrarrazoesNormalizado,
    },
    {
      etapa: 'Prazo de Julgamento',
      inicio: inicioJulgamento,
      fim: fimJulgamentoNormalizado,
    },
  ];
};

export const isPrazoRecursalVencido = (dataLimite, now = new Date()) => {
  if (!dataLimite) return false;
  const fim = normalizarFimPrazoRecursal(dataLimite);
  if (!fim || Number.isNaN(fim.getTime())) return false;
  return now.getTime() > fim.getTime();
};

export const identificarJanelaRecursalVigente = (prazos, now = new Date()) => {
  if (!prazos?.definidos) return null;

  if (!isPrazoRecursalVencido(prazos.dataLimiteRazoes, now)) return 'razoes';
  if (!isPrazoRecursalVencido(prazos.dataLimiteContrarrazoes, now)) {
    return 'contrarrazoes';
  }
  if (!isPrazoRecursalVencido(prazos.dataLimiteJulgamento, now)) {
    return 'julgamento';
  }
  return null;
};

export const calcularAcoesRecurso = (status, prazos, now = new Date()) => {
  if (!prazos?.definidos) {
    return {
      podeApresentarRazoes: false,
      podeEditarRazoes: false,
      podeExcluirRazoes: false,
      podeContrarrazoar: false,
      podeEditarContrarrazoes: false,
      podeExcluirContrarrazoes: false,
      podeConsiderar: false,
      podeJulgarAutoridade: false,
    };
  }

  const statusNormalizado = normalizarStatusRecurso(status);
  const janela = identificarJanelaRecursalVigente(prazos, now);
  const naJanelaRazoes = statusNormalizado === RECURSO_STATUS.CRIADO && janela === 'razoes';
  const naJanelaContrarrazoes =
    statusNormalizado === RECURSO_STATUS.CRIADO && janela === 'contrarrazoes';

  return {
    podeApresentarRazoes: naJanelaRazoes,
    podeEditarRazoes: naJanelaRazoes,
    podeExcluirRazoes: naJanelaRazoes,
    podeContrarrazoar: naJanelaContrarrazoes,
    podeEditarContrarrazoes: naJanelaContrarrazoes,
    podeExcluirContrarrazoes: naJanelaContrarrazoes,
    podeConsiderar: statusNormalizado === RECURSO_STATUS.CRIADO && janela === 'julgamento',
    podeJulgarAutoridade:
      statusNormalizado === RECURSO_STATUS.INDEFERIDO && janela === 'julgamento',
  };
};

export { feriadosNacionais, isBusinessDay };
